import { Button, Card, Flex, HStack } from '@chakra-ui/react';
import { useHttpQuery } from '@mono/http-hook';
import { FC, useMemo } from 'react';
import { useCurrentOrganization } from '../organizations/hooks';
import BreadcrumbNav, { BreadcrumbItem } from './components/BreadcrumbNav';
import CreateFileButton from './components/CreateFileButton';
import CreateFolderButton from './components/CreateFolderButton';
import {
  FileTreeEmptyOrg,
  FileTreeError,
  FileTreeFolderView,
  FileTreeLoading,
  FileTreePlaceholder,
} from './components/FileTreeContent';
import { FileBrowserProps, FileTreeResponse } from './types';

const joinSegments = (segments: string[]) =>
  segments.length === 0 ? '/' : `/${segments.join('/')}`;

const normalizeBrowserPath = (value?: string | null) => {
  if (!value) return '/';
  const trimmed = value.trim();
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const FileBrowser: FC<FileBrowserProps> = ({
  projectId,
  path,
  onChangePath,
  onOpenFile,
}) => {
  const organization = useCurrentOrganization();
  const normalizedPath = normalizeBrowserPath(path);
  const pathSegments = useMemo(
    () =>
      normalizedPath === '/' ? [] : normalizedPath.split('/').filter(Boolean),
    [normalizedPath]
  );

  const fileTreeQuery = useHttpQuery<FileTreeResponse>({
    queryKey: ['file-tree', organization?.id, projectId, normalizedPath],
    config: {
      method: 'get',
      url: organization
        ? `/api/v1/organizations/${organization.id}/projects/${projectId}/file-tree`
        : '',
      params: normalizedPath === '/' ? undefined : { path: normalizedPath },
      withCredentials: true,
    },
    options: {
      enabled: Boolean(organization?.id && projectId),
    },
  });

  const navigateToPath = (nextPath: string) => {
    const formatted = normalizeBrowserPath(nextPath);
    onChangePath(formatted === '/' ? '' : formatted);
  };

  const navigateToFileEditor = (nextPath: string) => {
    const formatted = normalizeBrowserPath(nextPath);
    onOpenFile(formatted === '/' ? '' : formatted);
  };

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    const crumbs: BreadcrumbItem[] = [{ label: 'root', path: '/' }];
    const segments: string[] = [];
    pathSegments.forEach((segment) => {
      segments.push(segment);
      crumbs.push({
        label: segment,
        path: joinSegments([...segments]),
      });
    });
    return crumbs;
  }, [pathSegments]);

  const parentPath = joinSegments(pathSegments.slice(0, -1));
  const isRoot = pathSegments.length === 0;
  const folderPathForCreation =
    fileTreeQuery.data?.type === 'folder'
      ? fileTreeQuery.data.path
      : parentPath;
  const canCreateEntries =
    Boolean(fileTreeQuery.data?.type === 'folder') && Boolean(organization);

  const handleTreeUpdated = () => {
    void fileTreeQuery.refetch();
  };

  const renderContent = () => {
    if (!organization) return <FileTreeEmptyOrg />;
    if (fileTreeQuery.isLoading) return <FileTreeLoading />;
    if (fileTreeQuery.isError)
      return <FileTreeError error={fileTreeQuery.error} />;
    if (!fileTreeQuery.data) return <FileTreePlaceholder />;

    if (fileTreeQuery.data.type === 'folder') {
      return (
        <FileTreeFolderView
          response={fileTreeQuery.data}
          onOpenFile={navigateToFileEditor}
          onOpenFolder={navigateToPath}
          formatTimestamp={formatTimestamp}
        />
      );
    }

    return null;
  };

  return (
    <Card.Root>
      <Card.Body gap="4">
        <Flex
          gap="3"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <BreadcrumbNav items={breadcrumbItems} onNavigate={navigateToPath} />
          <HStack gap="2" flexWrap="wrap">
            {organization ? (
              <>
                <CreateFolderButton
                  organizationId={organization.id}
                  projectId={projectId}
                  parentPath={folderPathForCreation}
                  onCreated={handleTreeUpdated}
                  isDisabled={!canCreateEntries}
                />
                <CreateFileButton
                  organizationId={organization.id}
                  projectId={projectId}
                  folderPath={folderPathForCreation}
                  onCreated={handleTreeUpdated}
                  isDisabled={!canCreateEntries}
                />
              </>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigateToPath(parentPath)}
              disabled={isRoot}
            >
              Up one level
            </Button>
          </HStack>
        </Flex>
        {renderContent()}
      </Card.Body>
    </Card.Root>
  );
};

export default FileBrowser;
